/**
 * Importación de consumos de proveedor desde un archivo de servicios realizados
 * (spec 005, contrato de integración §5.2 / §6). TODOS los DTO de los endpoints de
 * MedicalProtocols que usa la pantalla viven acá, para alinearlos en un solo lugar
 * si el backend ajusta las formas JSON.
 *
 * Identificadores (verificados contra MedicalProtocols.API):
 * - ProviderFiles: `ProviderId` int (= `Provider.id` del front) y `FileId` Guid (string).
 * - ProviderConsumedServices: `ProviderId` Guid (= `Provider.person.personIdentifier`) e `Id` Guid.
 * - ProtocolConsumedServices: `Id` Guid.
 */

/** Tipo de archivo de proveedor en MedicalProtocols (ProviderFileTypeEnum): 1 = factura, 2 = servicios realizados. */
export const PROVIDER_FILE_TYPE_INVOICE = 1;
export const PROVIDER_FILE_TYPE_SERVICES_PERFORMED = 2;

/** Tipos de archivo desde los que se puede importar (MP 029: factura o servicios realizados). */
export const IMPORTABLE_PROVIDER_FILE_TYPES = [PROVIDER_FILE_TYPE_INVOICE, PROVIDER_FILE_TYPE_SERVICES_PERFORMED];

/** Extensiones que IA.API normaliza (MP 029, "Archivos importables"). */
export const IMPORTABLE_FILE_EXTENSIONS = ['xlsx', 'xls', 'csv', 'pdf', 'docx'];

/** Confianza por debajo de la cual la importación marca el ítem (ProviderImport:MinConfidence = 0,6). */
export const LOW_CONFIDENCE_THRESHOLD = 0.6;

/** Campos del formato unificado que se mapean a una columna del archivo (contrato v2 §1). */
export type ImportMappingField = 'code' | 'name' | 'date' | 'value' | 'quantity' | 'patient' | 'currency';

export const IMPORT_MAPPING_FIELDS: ImportMappingField[] = ['code', 'name', 'date', 'value', 'quantity', 'patient', 'currency'];

/** Columna por campo: encabezado (o letra de Excel); `null` = sin columna; `value: "last"` = última columna de precio. */
export type ImportMappingColumns = Partial<Record<ImportMappingField, string | null>>;

/** Valor especial de `columns.value`: última columna de precio. */
export const LAST_PRICE_COLUMN = 'last';

/** Estrategia de normalización de IA.API ("document" sólo cuando la IA extrajo ítems de un documento sin tablas). */
export type ImportNormalizationStrategy = 'tabular' | 'document';

/** Override del mapeo (cualquier subconjunto; IA responde 400 ante propiedades desconocidas). */
export interface ImportMappingOverride {
  sheetName?: string | null;
  /** Fila física del encabezado (1-based). */
  headerRowIndex?: number | null;
  /** Filas del encabezado (1 o 2). */
  headerRowCount?: number | null;
  columns?: ImportMappingColumns;
  /** aaaa-mm-dd */
  documentDate?: string | null;
  defaultCurrencyCode?: string | null;
}

/** Plan de normalización que devolvió la IA (null con versiones anteriores de IA.API). */
export interface ImportNormalization {
  strategy: ImportNormalizationStrategy | string;
  /** Hoja, o "Tabla N (pág. P)" para tablas de PDF / DOCX. */
  sheetName?: string | null;
  headerRowIndex?: number | null;
  headerRowCount?: number | null;
  columns?: ImportMappingColumns | null;
  documentDate?: string | null;
  defaultCurrencyCode?: string | null;
  /** Confianza del plan (0..1). */
  confidence?: number | null;
  notes?: string[] | null;
}

/** Fila normalizada de la vista previa. */
export interface ImportPreviewItem {
  rowNumber: number;
  code?: string | null;
  name?: string | null;
  serviceDate?: string | null;
  amount?: number | null;
  currencyCode?: string | null;
  rawAmount?: string | null;
  warnings?: string[] | null;
  quantity?: number | null;
  patientReference?: string | null;
  sourceReference?: string | null;
  /** null en la estrategia tabular (determinística). */
  confidence?: number | null;
  // ---- vista previa congelada (spec 009, MedicalProtocols 031): todo opcional para convivir con un backend anterior ----
  /** Identificador estable del ítem dentro de la vista previa (es el que viaja en `selectedItemIds`). */
  id?: string;
  /** Página de origen (null en planillas). */
  page?: number | null;
  /** ORIGINAL, DUPLICADO, TRIPLICADO… de la página. */
  documentCopy?: string | null;
  /** `{cuit}-{cod}-{ptoVta}-{nro}` del comprobante de su página. */
  voucherKey?: string | null;
  /** 1..5 (`ImportDuplicateStatus`); el backend lo manda como número, se tolera el nombre. */
  duplicateStatus?: ImportDuplicateStatus | number | string | null;
  duplicateOf?: DuplicateOf | null;
  defaultSelected?: boolean;
  selectable?: boolean;
  suggestion?: ImportItemSuggestion | null;
  serviceResolution?: ImportItemServiceResolution | null;
}

// ---------------------------------------------------------------------------------------------
// Vista previa congelada (spec 009, MedicalProtocols 031 §1)
// ---------------------------------------------------------------------------------------------

/** MP ProviderImportItemDuplicateStatusEnum. */
export enum ImportDuplicateStatus {
  New = 1,
  CopyOfVoucher = 2,
  PossibleDuplicate = 3,
  AlreadyImportedThisFile = 4,
  AlreadyImportedOtherFile = 5,
}

export const IMPORT_DUPLICATE_STATUSES: ImportDuplicateStatus[] = [
  ImportDuplicateStatus.New,
  ImportDuplicateStatus.CopyOfVoucher,
  ImportDuplicateStatus.PossibleDuplicate,
  ImportDuplicateStatus.AlreadyImportedThisFile,
  ImportDuplicateStatus.AlreadyImportedOtherFile,
];

/** Estados que nacen seleccionados (RF-011) y estados seleccionables (el 4 no). */
export const DEFAULT_SELECTED_STATUSES: ImportDuplicateStatus[] = [ImportDuplicateStatus.New, ImportDuplicateStatus.PossibleDuplicate];
export const SELECTABLE_STATUSES: ImportDuplicateStatus[] = [
  ImportDuplicateStatus.New,
  ImportDuplicateStatus.CopyOfVoucher,
  ImportDuplicateStatus.PossibleDuplicate,
  ImportDuplicateStatus.AlreadyImportedOtherFile,
];

/** Tamaños de página de "Revisar ítems" (la API acepta 1..200; la primera página del POST es de 50). */
export const PREVIEW_ITEMS_PAGE_SIZES = [25, 50, 100, 200];
export const PREVIEW_ITEMS_DEFAULT_PAGE_SIZE = 50;

/** Emisor o receptor del comprobante (IA.API 003). */
export interface VoucherParty {
  taxId?: string | null;
  taxIdValid?: boolean | null;
  businessName?: string | null;
  address?: string | null;
  vatCondition?: string | null;
  grossIncomeNumber?: string | null;
  /** aaaa-mm-dd */
  activityStartDate?: string | null;
}

/** Cabecera del comprobante detectada por IA (con `copies` completado por MedicalProtocols). */
export interface VoucherHeader {
  issuer?: VoucherParty | null;
  receiver?: VoucherParty | null;
  /** Letra ("A"). */
  voucherType?: string | null;
  /** Código de ARCA ("01" = Factura A). */
  voucherTypeCode?: string | null;
  pointOfSale?: number | null;
  number?: number | null;
  issueDate?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  paymentDueDate?: string | null;
  cae?: string | null;
  caeDueDate?: string | null;
  total?: number | null;
  currencyCode?: string | null;
  /** ["ORIGINAL", "DUPLICADO", "TRIPLICADO"] */
  copies?: string[] | null;
  /** "rules" | "llm" | "ocr+rules" */
  source?: string | null;
  confidence?: number | null;
}

/** Otro archivo activo del proveedor con el mismo comprobante y consumos importados (RF-013). */
export interface VoucherAlreadyImported {
  providerFileId: string;
  fileName?: string | null;
  /** Relativo al tenant, para "Ver archivo". */
  blobPath?: string | null;
  importedAt?: string | null;
}

/** Resumen de la vista previa (RF-014). */
export interface ImportPreviewSummary {
  total: number;
  new: number;
  copyOfVoucher: number;
  possibleDuplicate: number;
  alreadyImportedThisFile: number;
  alreadyImportedOtherFile: number;
  defaultSelected: number;
  defaultSelectedAmount: number;
}

/** `{ itemId, rowNumber }` para los estados 2 y 3; `{ consumptionId, providerFileId, fileName, importedAt }` para el 5. */
export interface DuplicateOf {
  itemId?: string | null;
  rowNumber?: number | null;
  consumptionId?: string | null;
  providerFileId?: string | null;
  fileName?: string | null;
  importedAt?: string | null;
}

export type SuggestionStatus = 'Matched' | 'NotFound' | 'Ambiguous' | 'NoTemplateMatch' | 'NoTemplates';

export interface ReferenceExtracted {
  protocol?: string | null;
  patient?: string | null;
  center?: string | null;
  service?: string | null;
}

/** Sugerencia de protocolo y paciente de la plantilla de referencia (US5; nunca se aplica sola). */
export interface ImportItemSuggestion {
  status: SuggestionStatus;
  templateId?: string | null;
  templateName?: string | null;
  /** null con NoTemplateMatch / NoTemplates. */
  extracted?: ReferenceExtracted | null;
  /** Sólo con Matched. */
  protocolId?: number | null;
  protocolName?: string | null;
  patientNumberInProtocol?: string | null;
  /** null sin paciente extraído o sin padrón. */
  patientValid?: boolean | null;
}

export type ServiceSourceKeyKind = 'TemplateService' | 'Code' | 'Name';
export type ServiceResolutionOrigin = 'Remembered' | 'MatchedByCode' | 'MatchedByName' | 'WillCreate';

/** Servicio médico propuesto para el ítem (US6), calculado al leer la página. */
export interface ImportItemServiceResolution {
  sourceKeyKind: ServiceSourceKeyKind;
  sourceKey: string;
  origin: ServiceResolutionOrigin;
  /** null con WillCreate. */
  medicalServiceId?: number | null;
  code?: string | null;
  name?: string | null;
  variableDescriptionWarning?: boolean;
  /** Coincidió con un servicio desactivado (la importación automática lo reutiliza). */
  serviceInactive?: boolean;
}

/** Respuesta de POST .../import-consumptions/preview (ProviderConsumptionFileImportPreviewViewModel). */
export interface ImportPreview {
  fileId: string | number;
  fileName?: string | null;
  normalization?: ImportNormalization | null;
  availableColumns?: string[] | null;
  detectedColumns?: ImportMappingColumns | null;
  /** Con MP 031: la primera página (50) con la forma nueva del ítem. */
  items?: ImportPreviewItem[] | null;
  totalRows?: number | null;
  warnings?: string[] | null;
  alreadyImportedCount?: number | null;
  mappingConflict?: boolean | null;
  // ---- MedicalProtocols 031 (ausentes con un backend anterior: el asistente no muestra "Revisar ítems") ----
  previewId?: string | null;
  /** UTC: después, importar con esta vista previa responde 409 PreviewExpired. */
  expiresAt?: string | null;
  voucher?: VoucherHeader | null;
  voucherAlreadyImported?: VoucherAlreadyImported | null;
  summary?: ImportPreviewSummary | null;
}

/** Filtros de GET .../import-previews/{previewId}/items (e `item-ids`, sin paginado). */
export interface PreviewItemsFilter {
  duplicateStatus?: ImportDuplicateStatus[];
  /** Código, nombre, ubicación o referencia de paciente. */
  search?: string;
  /** "Aplicar a todos los iguales" (US2). */
  suggestedProtocolId?: number;
  suggestedPatientNumber?: string;
  /** Ítems con la misma clave de servicio (US6). */
  serviceSourceKey?: string;
}

export interface PreviewItemsQuery extends PreviewItemsFilter {
  pageNumber: number;
  /** 1..200 */
  pageSize: number;
  /** Por defecto true. */
  withSuggestions?: boolean;
}

export interface PreviewItemsPage {
  items: ImportPreviewItem[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
}

/** Ítem de `item-ids` (importe null = sin importe, cuenta 0). */
export interface PreviewItemAmount {
  id: string;
  amount?: number | null;
  currencyCode?: string | null;
}

/** `item-ids`: sólo ítems seleccionables que cumplen los filtros. */
export interface PreviewItemIds {
  items: PreviewItemAmount[];
  amount: number;
}

// ---- correspondencias de servicio del proveedor (MP 031 §2b, US6) ----

/** Correspondencia recordada "clave del proveedor → servicio médico" (GET providers/{int}/medical-service-mappings). */
export interface ProviderMedicalServiceMapping {
  id: string;
  /** `TemplateService`, `Code` o `Name` (se tolera el número 1..3 del enum de MP). */
  sourceKeyKind: ServiceSourceKeyKind | string | number;
  sourceKey: string;
  /** Texto original de un ítem con esa clave (se muestra como texto). */
  sampleText?: string | null;
  medicalServiceId: number;
  medicalServiceCode?: string | null;
  medicalServiceName?: string | null;
  /** UTC; la lista viene ordenada por último uso, más reciente primero. */
  lastUsedAt?: string | null;
  lastModifiedBy?: string | null;
}

export interface ServiceMappingsQuery {
  search?: string;
  pageNumber: number;
  /** 1..200 */
  pageSize: number;
}

export interface ServiceMappingsPage {
  items: ProviderMedicalServiceMapping[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
}

export const SERVICE_MAPPINGS_PAGE_SIZES = [25, 50, 100, 200];
export const SERVICE_MAPPINGS_DEFAULT_PAGE_SIZE = 50;

// ---- formatos de referencia de protocolo (MP 031 §3, US5) ----

/** Campo que captura una parte de la plantilla. */
export type ReferenceField = 'protocol' | 'patient' | 'center' | 'service';
export const REFERENCE_FIELDS: ReferenceField[] = ['protocol', 'patient', 'center', 'service'];

/** Parte de la plantilla: texto fijo, campo o tramo variable que se ignora. */
export type ReferenceTemplatePart = { kind: 'text'; value: string } | { kind: 'field'; field: ReferenceField } | { kind: 'any' };

/** Plantilla de referencia del proveedor (GET providers/{int}/protocol-reference-templates, activas e inactivas, en orden). */
export interface ProtocolReferenceTemplate {
  id: string;
  /** 1..100 */
  name: string;
  order: number;
  active: boolean;
  parts: ReferenceTemplatePart[];
  /** Texto del ítem con que se definió (≤ 500); se muestra como texto. */
  sampleText?: string | null;
  /** Id del usuario que la creó. */
  createdBy?: string | null;
  /** UTC: última modificación o alta. */
  lastModified?: string | null;
}

/** Cuerpo de POST (alta, queda al final) y PUT (edición; `active` sin valor conserva el estado). */
export interface SaveReferenceTemplateRequest {
  name: string;
  parts: ReferenceTemplatePart[];
  sampleText?: string | null;
  active?: boolean;
}

/** Cuerpo de POST .../protocol-reference-templates/test (no guarda nada). */
export interface TemplateTestRequest {
  parts: ReferenceTemplatePart[];
  /** ≤ 500 textos de ≤ 2000 caracteres. */
  texts: string[];
}

/** Resultado de probar la plantilla con un texto. */
export interface TemplateTestResult {
  text: string;
  matched: boolean;
  extracted?: ReferenceExtracted | null;
  protocolId?: number | null;
  protocolName?: string | null;
  patientNumberInProtocol?: string | null;
  patientValid?: boolean | null;
  /** Matched, NotFound, Ambiguous o NoTemplateMatch. */
  status: SuggestionStatus | string;
  /** Correspondencia recordada para la clave `service` extraída; null si no hay. */
  serviceMapping?: { medicalServiceId: number; name?: string | null } | null;
}

/** Límites del servidor (MP `ProviderProtocolReferenceTemplates`). */
export const REFERENCE_TEMPLATE_NAME_MAX = 100;
export const REFERENCE_TEMPLATE_SAMPLE_MAX = 500;
export const REFERENCE_TEMPLATE_TEXT_MAX = 500;
export const REFERENCE_TEMPLATE_MAX_ACTIVE = 20;
export const TEMPLATE_TEST_MAX_TEXTS = 500;
export const TEMPLATE_TEST_MAX_TEXT_LENGTH = 2000;

// ---- importación con selección (MP 031 §2) ----

export interface ImportAssignment {
  protocolId: number;
  /** ≤ 50 */
  patientNumberInProtocol?: string | null;
  patientId?: string | null;
}

export type ImportItemAssignment = ({ itemId: string } & ImportAssignment) | { itemId: string; unassigned: true };

/** Exactamente una de las tres formas. */
export type MedicalServiceChoice = { medicalServiceId: number } | { create: { code?: string | null; name: string } } | { auto: true };

export type ImportItemMedicalService = { itemId: string } & MedicalServiceChoice;

/** Campos del 400 por ítem. */
export type ImportItemErrorField = 'patient' | 'protocol' | 'medicalService' | 'selection';

/** `data.itemErrors[]` del 400 (itemId / rowNumber null si el error es de la asignación o el servicio general). */
export interface ImportItemError {
  itemId?: string | null;
  rowNumber?: number | null;
  field: ImportItemErrorField;
  message: string;
}

/** `data.reason` del 409 de la importación. */
export type ImportConflictReason = 'PreviewExpired' | 'ImportInProgress' | 'MappingConflict';

/** Conteo de excluidos por clasificación (job.result.excludedByStatus). */
export type ImportExcludedByStatus = Partial<
  Record<'new' | 'copyOfVoucher' | 'possibleDuplicate' | 'alreadyImportedThisFile' | 'alreadyImportedOtherFile', number>
>;

/** Respuesta de DELETE .../imported-consumptions. */
export interface DeleteImportedConsumptionsResult {
  fileId: string | number;
  deleted: number;
}

/** Estado de asignación del consumo importado (filtro `AssignmentStatus`). */
export type ImportAssignmentStatus = 'Unassigned' | 'Assigned' | 'Generated';

export const IMPORT_ASSIGNMENT_STATUSES: ImportAssignmentStatus[] = ['Unassigned', 'Assigned', 'Generated'];

/** Cuerpo de POST providers/{ProviderId}/files/{FileId}/import-consumptions. */
export interface ImportProviderFileConsumptionsRequest {
  /** "last" (por defecto en la API) o el nombre de la columna; atajo de `columns.value`. */
  valueColumn?: string;
  /** Override del mapeo; ausente = el plan de la IA. Con `previewId` no se manda (lo toma la API de la vista previa). */
  mappingOverride?: ImportMappingOverride;
  // ---- MedicalProtocols 031: importación con selección (sólo con `previewId`) ----
  previewId?: string;
  /** No vacío, sin repetidos y siempre explícito. */
  selectedItemIds?: string[];
  defaultAssignment?: ImportAssignment | null;
  itemAssignments?: ImportItemAssignment[];
  defaultMedicalService?: MedicalServiceChoice | null;
  itemMedicalServices?: ImportItemMedicalService[];
  rememberServiceMappings?: boolean;
}

/** Respuesta de la importación. */
export interface ImportProviderFileConsumptionsResult {
  fileId: string | number;
  totalRows: number;
  created: number;
  skippedExisting: number;
  medicalServicesCreated: number;
  medicalServicesReused: number;
  /** Servicios encontrados por nombre en filas sin código (MedicalProtocols 029 v2.8.1); ausente en versiones anteriores. */
  medicalServicesMatchedByName?: number;
  /** Valorizados con el valor del archivo (MP 030; ausente en versiones anteriores). */
  valuedByFileValue?: number;
  /** Valorizados con la lista de precios del proveedor (MP 030). */
  valuedByPriceList?: number;
  /** Sin valorizar (quedan con alarma) (MP 030). */
  unvalued?: number;
  // ---- MedicalProtocols 031 (null / ausentes sin vista previa) ----
  previewId?: string | null;
  /** Ítems seleccionados que se intentaron importar. */
  selected?: number | null;
  /** Nacieron marcados y el usuario los desmarcó. */
  excludedByUser?: number | null;
  /** Nacieron desmarcados por su clasificación y siguieron así (o no eran seleccionables). */
  excludedByStatus?: ImportExcludedByStatus | null;
  assigned?: number | null;
  assignmentWarnings?: string[] | null;
  servicesChosen?: number | null;
  servicesCreatedOnRequest?: number | null;
  mappingsSaved?: number | null;
  warnings: string[];
}

/** Consumo de proveedor (ProviderConsumptionViewModel) con los campos de la importación. */
export interface ImportedProviderConsumption {
  id: string;
  providerId?: string | null;
  providerName?: string | null;
  medicalServiceId?: number | null;
  medicalServiceName?: string | null;
  medicalServiceCode?: string | null;
  serviceName?: string | null;
  serviceDueDate?: string | null;
  status?: number;
  protocolId?: number | null;
  protocolName?: string | null;
  patientNumberInProtocol?: string | null;
  patientId?: number | null;
  medicalRecordNumber?: string | null;
  contractId?: number | null;
  contractConceptId?: number | null;
  conceptDescription?: string | null;
  currencyCode?: string | null;
  amount?: number | null;
  origin?: number;
  markedAsHaveWarnings?: boolean;
  warningsComments?: string | null;
  // importación (§5.1)
  providerFileId?: string | number | null;
  sourceRowNumber?: number | null;
  importAmount?: number | null;
  importCurrencyCode?: string | null;
  protocolConsumedServiceId?: string | null;
  assignedAt?: string | null;
  generatedAt?: string | null;
  // importación normalizada por IA (MP 029)
  quantity?: number | null;
  /** Referencia al paciente leída del archivo (sólo informativa). */
  patientReference?: string | null;
  /** Ubicación en el archivo: "fila N", "Hoja!F12", "pág. 2, tabla 1, fila 5", "pág. P, línea L". */
  sourceReference?: string | null;
  /** Confianza de la IA (0..1); null en la estrategia tabular. */
  importConfidence?: number | null;
}

/** Filtros de GET providers/{ProviderId}/consumptions para los consumos importados. */
export interface ImportedConsumptionsFilter {
  providerFileId?: string;
  assignmentStatus?: ImportAssignmentStatus;
  pageNumber: number;
  pageSize: number;
}

/** Página de consumos importados. */
export interface ImportedConsumptionsPage {
  items: ImportedProviderConsumption[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
}

/** Cuerpo de PUT providers/consumptions/assignment. */
export interface ProviderConsumptionAssignmentRequest {
  ids: string[];
  protocolId: number;
  patientNumberInProtocol: string;
  /** Sólo si se conoce (la lista de pacientes del protocolo hoy no lo expone). */
  patientId?: number;
}

/** Cuerpo de POST providers/consumptions/generate-protocol-consumptions. */
export interface GenerateProtocolConsumptionsRequest {
  ids: string[];
}

export interface GenerateProtocolConsumptionsSkipped {
  id: string;
  reason: string;
}

/** Respuesta de la generación de consumos de protocolo. */
export interface GenerateProtocolConsumptionsResult {
  generated: number;
  withWarnings: number;
  skipped: GenerateProtocolConsumptionsSkipped[];
}

// ---------------------------------------------------------------------------------------------
// Importación en segundo plano (spec 008, MedicalProtocols 030)
// ---------------------------------------------------------------------------------------------

/** Estado del job de importación (ProviderFileImportJobs.Status). */
export enum ProviderImportJobStatus {
  Queued = 1,
  Running = 2,
  Succeeded = 3,
  Failed = 4,
}

/**
 * Job de importación (respuesta 202 de POST .../import-consumptions y de los GET de import-jobs).
 * `status` puede llegar como número (1..4) o como nombre ("Queued", "Running", ...): se normaliza con
 * `importJobStatusOf` (ImportJobs.Functions) para tolerar la forma final del backend.
 */
export interface ProviderImportJob {
  id: string;
  /** Provider.id (int). */
  providerId: number;
  /** Provider.person.personIdentifier (Guid). */
  providerIdentifier?: string | null;
  providerFileId: string;
  fileName?: string | null;
  status: ProviderImportJobStatus | number | string;
  /** Etapa en texto corto ("En cola", "Leyendo con IA", ...), en el idioma del backend. */
  stage?: string | null;
  /** 0..100 */
  progressPercent?: number | null;
  requestedAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  requestedByUserName?: string | null;
  /** Resumen de la importación (misma forma que la respuesta sincrónica anterior); null mientras no terminó bien. */
  result?: ImportProviderFileConsumptionsResult | null;
  errorMessage?: string | null;
  errors?: string[] | null;
}

/** Cuerpo `data` del 409 "ya hay una importación en curso de este archivo" (MP 031 suma `reason`). */
export interface ImportJobConflictData {
  jobId: string;
  reason?: ImportConflictReason | null;
}

/** ErrorType del 409 con `data.jobId` (sin `JobId` es el 409 de "otro mapeo" de la spec 007). */
export interface ImportJobConflictError {
  ErrorCode: number;
  ErrorMessage: string;
  Errors: string[];
  JobId: string;
  Reason?: ImportConflictReason;
}

/**
 * ErrorType de la importación con lo que trae el cuerpo de error (MP 031): `Reason` del 409 (`data.reason`), `JobId` del
 * 409 ImportInProgress y `ItemErrors` del 400 (`data.itemErrors`).
 */
export interface ImportRequestError {
  ErrorCode: number;
  ErrorMessage: string;
  Errors: string[];
  JobId?: string;
  Reason?: ImportConflictReason;
  ItemErrors?: ImportItemError[];
}

/** Lo que puede devolver "Importar": el job (202) o, con un backend anterior a MP 030, el resumen sincrónico. */
export type ImportStartResponse = ProviderImportJob | ImportProviderFileConsumptionsResult | undefined;
